import { useEffect, type ReactNode } from "react"
import Head from "next/head"
import { usePathname } from "next/navigation"
import Script from "next/script"
import { CurrentUser, UserPermission } from "@/types/user"

import { CF_CGI_URL } from "@/lib/cloudflare-loader"
import { mtLocation } from "@/lib/mt-location"
import { ClientRender } from "@/components/client-render"
import { CookiesBanner } from "@/components/cookies-banner"
import { IOSAppBannerMeta } from "@/components/ios-app-banner-meta"
import { BusinessContactForm } from "@/components/marketing/business-contact-form"
import { MarketingFooter } from "@/components/marketing/marketing-footer"
import { MarketingHeader } from "@/components/marketing/marketing-header"

export default function MarketingLayout({
  children,
  initUser,
  permissions,
  knockoutHeader,
}: {
  children: ReactNode
  initUser?: CurrentUser
  permissions?: UserPermission[]
  knockoutHeader?: boolean
}) {
  const pathname = usePathname()
  const isBusiness = pathname.startsWith("/business")
  const shouldIncludeHubspot =
    isBusiness ||
    pathname.startsWith("/about") ||
    pathname.startsWith("/team") ||
    pathname.startsWith("/press")

  useEffect(() => {
    if (mtLocation.hash === "#contact") {
      window.scrollTo(0, 0)

      const scrollToContact = () => {
        const contactElement = document.getElementById("contact")
        if (!contactElement) return

        const rect = contactElement.getBoundingClientRect()
        const scrollPadding = 64
        const isAtTarget = Math.abs(rect.top - scrollPadding) < 10

        if (!isAtTarget) {
          contactElement.scrollIntoView()
        }

        return isAtTarget
      }

      const startScrolling = () => {
        let attempts = 0
        const maxAttempts = 10

        const intervalId = setInterval(() => {
          attempts++
          const success = scrollToContact()

          if (success || attempts >= maxAttempts) {
            clearInterval(intervalId)
          }
        }, 300)
      }

      setTimeout(startScrolling, 100)
    }
  }, [])

  return (
    <>
      <Head>
        <IOSAppBannerMeta />
        <style>{`html { scroll-padding-top: 4rem }`}</style>
        <link rel="preconnect" href={CF_CGI_URL} />
      </Head>
      <div className="flex min-h-screen flex-col">
        <MarketingHeader
          initUser={initUser}
          knockout={knockoutHeader}
          isBusiness={isBusiness}
          permissions={permissions}
        />
        <div className="flex flex-1 flex-col">
          {children}
          {isBusiness && <BusinessContactForm />}
        </div>
        <MarketingFooter />
        <ClientRender>
          <CookiesBanner />
        </ClientRender>
      </div>
      {shouldIncludeHubspot && (
        <Script
          id="hubspot-script"
          src="//js.hs-scripts.com/46862570.js"
          // after the page's own work: it ran ~150ms of JS during hydration
          strategy="lazyOnload"
        />
      )}
    </>
  )
}
